import Indicators from '../sliders/Indicators'
import HomepageHeroSliderImage from '@components/HomepageHeroSliderImage'
import { Container } from '@components/gassan-ui'
import { Button } from '@components/rolex-ui/Button'
import { Heading } from '@components/rolex-ui/Heading'
import { IconButton } from '@components/rolex-ui/IconButton'
import { cn } from '@lib/cn'
import { useHomepageHeroSlider } from '@lib/hooks/use-homepage-hero-slider'
import { RolexHeroSliderItem } from '@lib/kontent/models/RolexHeroSliderItem'
import { FC } from 'react'
import { useSwipeable } from 'react-swipeable'

type RolexHeroSliderProps = {
  items: RolexHeroSliderItem[]
}

export const RolexHeroSlider: FC<RolexHeroSliderProps> = ({ items }) => {
  const slideLength = items.length
  const { currentIndex, nextSlide, prevSlide } = useHomepageHeroSlider(slideLength)
  const activeSlide = items[currentIndex]
  const nextActiveSlide = slideLength > 1 ? items[(currentIndex + 1) % slideLength] : activeSlide
  const alignLeft = activeSlide.align_content.value[0]?.codename === 'left'
  const colorIsWhite = activeSlide.text_color.value[0]?.codename === 'light'

  const handlers = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => prevSlide(),
    preventDefaultTouchmoveEvent: true,
  })

  return (
    <section className="relative">
      <div
        key={activeSlide.title.value}
        className={cn(
          'relative mx-auto w-full max-w-[2560px] overflow-hidden',
          'before:content before:block before:pt-[122%] md:before:pt-[70%] lg:before:pt-[55%] xl:before:pt-[47%] xxl:before:pt-[725px]',
        )}
        {...handlers}
      >
        <HomepageHeroSliderImage {...activeSlide} slideToPreload={nextActiveSlide} />
      </div>
      <Container className="absolute bottom-16 left-0 right-0 z-[2] grid grid-cols-6 items-center md:top-1/2 md:!w-[calc(100%-10rem)] md:-translate-y-1/2 md:grid-cols-12 xl:!w-[calc(100%-14rem)]">
        <div
          className={cn(
            'col-span-6 flex flex-col items-start text-rolexBlack',
            !alignLeft && 'md:col-start-7',
            colorIsWhite && 'text-white',
          )}
        >
          <Heading variant="headline26">{activeSlide.pretitle.value}</Heading>
          <Heading variant="headline70" className="mb-4">
            {activeSlide.title.value}
          </Heading>
          <Button as="link" variant="primary" href={activeSlide.button_link.value}>
            {activeSlide.button_text.value}
          </Button>
        </div>
      </Container>
      {items.length > 1 && (
        <>
          <Container as="div" className="absolute bottom-5 z-[2] md:bottom-8 lg:bottom-10">
            <Indicators activeIndex={currentIndex} variant="animated" count={items.length} />
          </Container>
          <Container
            as="div"
            className="absolute left-0 right-0 top-1/2 z-[2] hidden -translate-y-10 justify-between md:flex"
          >
            <IconButton variant="light" as="button" icon="chevron-left" onClick={nextSlide} />
            <IconButton variant="light" as="button" icon="chevron-right" onClick={prevSlide} />
          </Container>
        </>
      )}
    </section>
  )
}
